import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export interface SearchableSelectOption {
  value: number;
  label: string;
  sublabel?: string;
}

interface SearchableSelectProps {
  value: number;
  options: SearchableSelectOption[];
  onChange: (value: number, option?: SearchableSelectOption) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  allowClear?: boolean;
  /** Max options rendered at once (keeps long lists snappy). */
  maxResults?: number;
}

const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[đ]/g, "d");

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  options,
  onChange,
  placeholder = "Pretraži...",
  className = "",
  disabled = false,
  allowClear = true,
  maxResults = 100,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  const selected = useMemo(
    () => (value ? options.find((o) => o.value === value) : undefined),
    [value, options]
  );

  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    const base = q
      ? options.filter((o) => {
          const hay = normalize(`${o.label} ${o.sublabel ?? ""}`);
          return hay.includes(q);
        })
      : options;
    return base.slice(0, maxResults);
  }, [query, options, maxResults]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  // The menu renders in a portal on <body>, so its position has to be
  // computed from the control's viewport rect. Anchoring it inside the
  // control instead meant any ancestor with `overflow` (the page section,
  // the modal, a table scroller) clipped it to a single visible option.
  const updatePosition = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const gap = 4;
    const margin = 8;
    const spaceBelow = window.innerHeight - rect.bottom - gap - margin;
    const spaceAbove = rect.top - gap - margin;
    const flipUp = spaceBelow < 180 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(280, Math.max(120, flipUp ? spaceAbove : spaceBelow));

    setMenuPos({
      top: flipUp ? rect.top - gap - maxHeight : rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      maxHeight,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updatePosition();
  }, [open, filtered.length, updatePosition]);

  useEffect(() => {
    if (!open) return;

    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inControl = containerRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);
      if (!inControl && !inMenu) {
        setOpen(false);
        setQuery("");
      }
    };

    // `true` captures scrolls on any ancestor, not just the window.
    const onReflow = () => updatePosition();

    document.addEventListener("mousedown", onClickOutside);
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [open, updatePosition]);

  const choose = (opt: SearchableSelectOption) => {
    onChange(opt.value, opt);
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(0, undefined);
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (open && filtered[highlight]) {
        e.preventDefault();
        choose(filtered[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <div className={`searchable-select ${open ? "is-open" : ""}`} ref={containerRef}>
      <div className="searchable-select-control">
        <input
          ref={inputRef}
          type="text"
          className={className}
          disabled={disabled}
          value={open ? query : selected?.label ?? ""}
          placeholder={selected ? selected.label : placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          onKeyDown={onKeyDown}
        />
        {allowClear && selected && !disabled && (
          <button
            type="button"
            className="searchable-select-clear"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clear}
            aria-label="Očisti odabir"
          >
            ✕
          </button>
        )}
      </div>
      {open &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            className="searchable-select-menu"
            style={{
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
            }}
          >
            {filtered.length === 0 ? (
              <div className="searchable-select-empty">Nema rezultata</div>
            ) : (
              filtered.map((opt, i) => (
                <div
                  key={opt.value}
                  className={`searchable-select-option ${
                    i === highlight ? "is-highlighted" : ""
                  } ${opt.value === value ? "is-selected" : ""}`}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(opt)}
                >
                  <span className="searchable-select-option-label">{opt.label}</span>
                  {opt.sublabel && (
                    <span className="searchable-select-option-sub">{opt.sublabel}</span>
                  )}
                </div>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default SearchableSelect;
